import { IIndexPreset } from '@/interfaces/benchmarks/index/IIndexPreset.ts';
import { indexApi } from '@/api/powerMarkets/indexApi.ts';
import { defineStore } from 'pinia';
import {
    computed, ref,
} from 'vue';
import { IndexTypeEnum, ScenarioEnum } from '@/enums/benchmarking/IndexEnums.ts';
import { IIdName } from '@/interfaces/general/IIdName.ts';
import {
    EU_PRESET_ICON_COLOURS, PRESET_BESS_REVENUE, PRESET_CCGT_REVENUE, PRESET_BESS_SKIP_RATES, PRESET_CCGT_SKIP_RATES,
    EURO_INDEX_PRESET_IDS, DEFAULT_INDEX_OPTIONS, DEFAULT_INDEX_OVERRIDES, DEFAULT_INDEX_SELECTED_DATE_MODEL,
    PRESET_CDPQ_BESS_REVENUE, PRESET_CDPQ_FIRST_HYDRO, PRESET_CDPQ_FIRST_HYDRO_CDPQ,
    PRESET_CDPQ_COMPARABLE_BESS, PRESET_CDPQ_BASE, PRESET_CDPQ_BESS_REVENUE_FIXED,
} from '@/constants/benchmarks/IndexConstants.ts';
import {
    IIndex, IEuropeanIndex, IIndexCountry,
} from '@/interfaces/benchmarks/index/IIndex.ts';
import { useUserSettingsStore } from '@/stores/userSettingsStore.ts';
import { useUserAccessStore } from '@/stores/userAccessStore.ts';
import { IIndexGroup } from '@/interfaces/benchmarks/index/IIndexGroup.ts';
import { isGbIndex } from '@/utils/benchmarks/indexUtil.ts';
import { IIndexCountryConfiguration } from '@/interfaces/benchmarks/index/IIndexDataResponse.ts';
import { cloneDeep } from '@/utils/global.ts';

// Only used to map country names to their country codes for presets and icons.
// In future could be put on the country configuration api call.
const INDEX_COUNTRY_IDS_BY_NAME: Record<string, string> = {
    Belgium: 'BE',
    France: 'FR',
    Germany: 'DE',
    'Great Britain': 'Gb',
    Netherlands: 'NL',
    Poland: 'PL',
    Spain: 'ES',
};

export const useIndexStore = defineStore('indexStore', () => {
    const cdpqPresets = [PRESET_CDPQ_BESS_REVENUE, PRESET_CDPQ_FIRST_HYDRO, PRESET_CDPQ_FIRST_HYDRO_CDPQ, PRESET_CDPQ_COMPARABLE_BESS, PRESET_CDPQ_BASE, PRESET_CDPQ_BESS_REVENUE_FIXED];
    const allPresetIndices = ref<Array<IIndexPreset>>([
        PRESET_BESS_REVENUE,
        PRESET_CCGT_REVENUE,
        PRESET_BESS_SKIP_RATES,
        PRESET_CCGT_SKIP_RATES,
        ...cdpqPresets,
    ]);
    const hasDevDashboardModules = computed<boolean>(() => (
        useUserAccessStore().userAccess.enact.shortTermMarkets?.hasDevDashboardModules === true
    ));
    const hasAssetMonitoringDefaults = computed<boolean>(() => (
        useUserAccessStore().userAccess.enact.shortTermMarkets?.hasAssetMonitoringDefaults === true
    ));
    const restrictedPresetIds = new Set<string>(cdpqPresets.map(preset => preset.id));
    const availablePresetIndices = computed<Array<IIndexPreset>>(() => (
        hasDevDashboardModules.value
            ? allPresetIndices.value
            : allPresetIndices.value.filter(preset => !restrictedPresetIds.has(preset.id))
    ));
    const visiblePresetIndices = computed<Array<IIndexPreset>>(() => (
        availablePresetIndices.value.filter(preset => (
            !restrictedPresetIds.has(preset.id) || hasAssetMonitoringDefaults.value
        ))
    ));
    const allIndexCountryConfigurations = ref<Array<IIndexCountryConfiguration>>([]);
    const storedIndicesForComparisonByGroup = ref<Record<string, Array<string>>>({});

    const loadedEuropeanPresetCountryKeys = ref<Set<string>>(new Set());
    const europeanPresetRequests = new Map<string, Promise<void>>();
    let countryConfigurationsRequest: Promise<void> | null = null;

    function convertPresetToDefaultGroup (preset: IIndexPreset): IIndexGroup {
        return {
            ...cloneDeep(preset),
            options: cloneDeep(preset.options ?? DEFAULT_INDEX_OPTIONS),
            selectedDateModel: cloneDeep(preset.selectedDateModel ?? DEFAULT_INDEX_SELECTED_DATE_MODEL),
            overrides: { ...DEFAULT_INDEX_OVERRIDES },
        };
    }

    const defaultIndexGroups = computed<Array<IIndexGroup>>(() => availablePresetIndices.value.map(convertPresetToDefaultGroup));
    const visibleDefaultIndexGroups = computed<Array<IIndexGroup>>(() => visiblePresetIndices.value.map(convertPresetToDefaultGroup));

    const availableEuropeanCountries = computed<Record<string, IIndexCountryConfiguration>>(() => Object.fromEntries(
        allIndexCountryConfigurations.value
            .filter(config => config.isEuropeCountry)
            .map(config => [config.country.id, config]),
    ));

    function getIndexCountryIdFromName (name: string): string {
        if (name === 'Gb' || name === 'GB') return 'Gb';

        const configuredCountry = allIndexCountryConfigurations.value
            .find(config => config.country.name === name || config.country.id === name)
            ?.country;

        return INDEX_COUNTRY_IDS_BY_NAME[configuredCountry?.name ?? name] ?? '';
    }

    const allIndexGroups = computed<Array<IIndexGroup>>(() => {
        const userSettingsStore = useUserSettingsStore();
        return Object.values(userSettingsStore.userSettings.indexGroupsWithOrder.objectsToStore).map(x => x.data);
    });

    const restrictedGbIndexIds = new Set<string>(
        cdpqPresets.flatMap(preset => Object.values(preset.indices))
            .map(index => index.id!),
    );

    function isRestrictedIndex (index: IIndex): boolean {
        return isGbIndex(index) && index.id !== undefined && restrictedGbIndexIds.has(index.id);
    }

    function isRestrictedIndexGroup (group: IIndexGroup): boolean {
        return Object.values(group.indices).some(isRestrictedIndex);
    }

    const allPresetIndicesByCountry = computed<Record<string, Record<string, IIndex>>>(() => {
        const out: Record<string, Record<string, IIndex>> = {};
        visiblePresetIndices.value.forEach((preset) => {
            out[preset.countryId] ??= {};
            if (!out[preset.countryId]) return;
            out[preset.countryId] = { ...out[preset.countryId], ...preset.indices };
        });
        return out;
    });

    const allUserIndicesByCountry = computed<Record<string, Record<string, IIndex>>>(() => {
        const out: Record<string, Record<string, IIndex>> = {};
        const canSeeRestrictedIndices = hasDevDashboardModules.value;
        allIndexGroups.value.map(x => x.indices).forEach((indexCollection) => {
            Object.keys(indexCollection).forEach((key) => {
                const index = indexCollection[key];
                if (!canSeeRestrictedIndices && isRestrictedIndex(index)) return;
                if (isGbIndex(index)) {
                    out.Gb ??= {};
                    out.Gb = { ...out.Gb, [key]: index };
                } else {
                    out[index.country] ??= {};
                    out[index.country] = { ...out[index.country], [key]: index };
                }
            });
        });
        return out;
    });

    function getIndexCountryFromAccessKey (accessKey: string): IIndexCountry {
        if (accessKey === 'Gb') {
            return {
                name: 'Great Britain',
                accessKey,
                countryCode: 'Gb',
                indexTypes: [IndexTypeEnum.Operational, IndexTypeEnum.SkipRates],
            };
        }

        const configuredCountry = allIndexCountryConfigurations.value
            .find(config => config.country.name === accessKey || config.country.id === accessKey)
            ?.country;
        const countryName = configuredCountry?.name ?? accessKey;
        const countryId = getIndexCountryIdFromName(countryName);

        return {
            name: countryName,
            accessKey,
            countryCode: countryId,
            indexTypes: [IndexTypeEnum.Optimisation],
        };
    }
    const configuredIndexCountries = computed<Array<IIndexCountry>>(() => allIndexCountryConfigurations.value
        .map(config => getIndexCountryFromAccessKey(config.country.id))
        .filter(country => country.countryCode !== ''));

    const userBasicAccessIndexCountries = computed<Array<IIndexCountry>>(() => {
        const userAccessStore = useUserAccessStore();
        const countriesWithAccessList = userAccessStore.userAccess.enact.shortTermMarkets?.indexCountryAccess ?? [];

        const publicIndexCountries = allIndexCountryConfigurations.value
            .filter(config => config.publicAccess)
            .map(config => config.country.id);
        return [...new Set([...publicIndexCountries, ...countriesWithAccessList])]
            .map(getIndexCountryFromAccessKey)
            .filter(country => country.countryCode !== '');
    });

    const userCustomAccessIndexCountries = computed<Array<IIndexCountry>>(() => {
        const userAccessStore = useUserAccessStore();
        const countriesWithAccessList = userAccessStore.userAccess.enact.shortTermMarkets?.customIndexCountryAccess ?? [];
        return countriesWithAccessList.map(getIndexCountryFromAccessKey);
    });

    function updateStoredIndicesForComparisonByGroup (groupId: string, indices: Array<string>): void {
        storedIndicesForComparisonByGroup.value[groupId] = indices;
    }

    function buildEuroPresetFromResponse (countryMeta: IIdName, response: Record<string, IEuropeanIndex>): IIndexPreset {
        const backendKey = countryMeta.name;
        return {
            id: EURO_INDEX_PRESET_IDS[getIndexCountryIdFromName(countryMeta.name)],
            name: `${countryMeta.name} Defaults`,
            description: `Presets for ${countryMeta.name} Default Indices`,
            countryId: getIndexCountryIdFromName(countryMeta.name),
            icon: 'chart-line-alt',
            iconColour: EU_PRESET_ICON_COLOURS[backendKey] ?? 'var(--lcpBlue)',
            indices: Object.fromEntries(
                Object.entries(response).map(([key, value]) => [
                    key,
                    {
                        ...value,
                        country: backendKey,
                        selectedScenario: ScenarioEnum.Medium,
                        indexType: IndexTypeEnum.Optimisation,
                    },
                ]),
            ),
            indexNameOrder: Object.keys(response),
        };
    }

    async function fetchEuroPresetForCountry (countryMeta: IIdName): Promise<void> {
        const backendKey = countryMeta.name;
        if (loadedEuropeanPresetCountryKeys.value.has(backendKey)) return;

        let request = europeanPresetRequests.get(backendKey);
        if (!request) {
            request = indexApi.getDefaultEuropeIndexData({ country: backendKey }).then(response => {
                allPresetIndices.value.push(buildEuroPresetFromResponse(countryMeta, response));
                loadedEuropeanPresetCountryKeys.value.add(backendKey);
            }).finally(() => europeanPresetRequests.delete(backendKey));
            europeanPresetRequests.set(backendKey, request);
        }
        await request;
    }

    async function getAvailableCountries (): Promise<void> {
        countryConfigurationsRequest ??= indexApi.getAllIndexCountryConfigurations().then(configurations => {
            allIndexCountryConfigurations.value = configurations;
        }).finally(() => { countryConfigurationsRequest = null; });
        await countryConfigurationsRequest;
    }

    async function ensureDefaultIndexGroup (groupId: string): Promise<void> {
        if (defaultIndexGroups.value.some(group => group.id === groupId)) return;
        const countryCode = Object.keys(EURO_INDEX_PRESET_IDS).find(code => EURO_INDEX_PRESET_IDS[code] === groupId);
        if (!countryCode) return;
        if (!allIndexCountryConfigurations.value.length) await getAvailableCountries();
        const country = allIndexCountryConfigurations.value.find(config => (
            config.isEuropeCountry && getIndexCountryIdFromName(config.country.id) === countryCode
        ))?.country;
        if (country) await fetchEuroPresetForCountry(country);
    }

    function isDefaultIndexGroup (groupId: string): boolean {
        return allPresetIndices.value.some(preset => preset.id === groupId)
            || Object.values(EURO_INDEX_PRESET_IDS).includes(groupId);
    }

    async function fetchAllEuroPresetIndices (): Promise<void> {
        await getAvailableCountries();
        await Promise.all(
            Object.values(availableEuropeanCountries.value).map((config) => fetchEuroPresetForCountry(config.country)),
        );
    }

    return {
        allPresetIndices,
        availablePresetIndices,
        visiblePresetIndices,
        defaultIndexGroups,
        visibleDefaultIndexGroups,
        ensureDefaultIndexGroup,
        isDefaultIndexGroup,
        allIndexGroups,
        allPresetIndicesByCountry,
        allUserIndicesByCountry,
        availableEuropeanCountries,
        allIndexCountryConfigurations,
        configuredIndexCountries,
        userBasicAccessIndexCountries,
        userCustomAccessIndexCountries,
        isRestrictedIndex,
        isRestrictedIndexGroup,
        storedIndicesForComparisonByGroup,
        fetchEuroPresetForCountry,
        fetchAllEuroPresetIndices,
        getAvailableCountries,
        updateStoredIndicesForComparisonByGroup,
    };
});
